import FontAwesomeIcon from "../../components/fontawesome/FontAwesomeIcon";
import {faChevronLeft, faClock} from "@fortawesome/free-solid-svg-icons";
import {createSignal, Show, For, createEffect} from "solid-js";
import moment from "moment";
import 'moment/locale/fr';
import {useStoreon} from "@storeon/solidjs";
import {Link, useParams} from "@solidjs/router";
import {Form, Button, Alert} from "solid-bootstrap";

export default ({handleBooking}) => {
    const [state, dispatch] = useStoreon();
    const params = useParams();

    const [selectedDate, setSelectedDate] = createSignal();
    const [currentDate, setCurrentDate] = createSignal("");
    createEffect(() => { params.activityId; setCurrentDate("") });

    // État du formulaire de liste d'attente
    const [waitlistSlot, setWaitlistSlot] = createSignal(null);
    const [waitlistEmail, setWaitlistEmail] = createSignal("");
    const [waitlistFirstName, setWaitlistFirstName] = createSignal("");
    const [waitlistLastName, setWaitlistLastName] = createSignal("");
    const [waitlistQty, setWaitlistQty] = createSignal(1);
    const [waitlistAlert, setWaitlistAlert] = createSignal(null);
    const [waitlistSubmitting, setWaitlistSubmitting] = createSignal(false);
    // Confirmation d'incrément : quand une inscription existe déjà, on propose
    // d'ajouter les places demandées au total existant (validation explicite).
    const [pendingIncrement, setPendingIncrement] = createSignal(null);
    const [incrementSubmitting, setIncrementSubmitting] = createSignal(false);

    createEffect(() => {
        if (state.waitingListResult === null) return;
        const result = state.waitingListResult;
        if (result && !result.graphQLErrors && result.success) {
            setWaitlistAlert({ type: "success", message: "Vous avez été ajouté à la liste d'attente. La réservation sera automatique si une place se libère." });
            setWaitlistSlot(null);
        } else if (result && result.graphQLErrors) {
            setWaitlistAlert({ type: "danger", message: "Une erreur est survenue. Veuillez réessayer." });
        } else if (result && result.errorCode === "SLOT_NOT_FULL") {
            setWaitlistAlert({ type: "warning", message: "Des places sont disponibles pour ce créneau, inutile de s'inscrire sur la liste d'attente." });
            setWaitlistSlot(null);
        } else if (result && result.errorCode === "ALREADY_ON_WAITING_LIST") {
            // Une inscription existe déjà : on propose d'incrémenter la quantité
            // en affichant le total avant validation. On capture l'email ici
            // (et non au moment du confirm) pour éviter qu'une modification du
            // champ entre-temps ne désynchronise la résolution côté serveur.
            setPendingIncrement({
                slot: waitlistSlot(),
                email: state.currentUser ? null : waitlistEmail(),
                existingQuantity: result.existingQuantity,
                additionalQuantity: parseInt(waitlistQty(), 10),
                proposedTotal: result.proposedTotal,
            });
            setWaitlistAlert(null);
        } else if (result && result.errorCode === "INVALID_INPUT") {
            setWaitlistAlert({ type: "danger", message: "Les informations saisies sont invalides. Vérifiez votre adresse e-mail et le nombre de places." });
        } else {
            setWaitlistAlert({ type: "danger", message: "Une erreur est survenue. Veuillez réessayer." });
        }
        dispatch("clearWaitingListResult");
        setWaitlistSubmitting(false);
    });

    createEffect(() => {
        if (state.incrementWaitingListResult === null) return;
        const result = state.incrementWaitingListResult;
        if (result && !result.graphQLErrors && result.success) {
            setWaitlistAlert({ type: "success", message: `Inscription mise à jour : ${result.proposedTotal} place(s) au total sur la liste d'attente.` });
            setWaitlistSlot(null);
            setPendingIncrement(null);
        } else if (result && result.graphQLErrors) {
            setWaitlistAlert({ type: "danger", message: "Une erreur est survenue lors de la mise à jour. Veuillez réessayer." });
        } else if (result && result.errorCode === "ENTRY_NOT_FOUND") {
            setWaitlistAlert({ type: "warning", message: "Votre inscription n'est plus en attente (elle a peut-être été attribuée). Vous pouvez réessayer de vous inscrire." });
            setPendingIncrement(null);
        } else if (result && result.errorCode === "SLOT_NOT_FULL") {
            setWaitlistAlert({ type: "warning", message: "Des places sont désormais disponibles pour ce créneau, inutile d'incrémenter la liste d'attente. Vous pouvez réserver directement." });
            setPendingIncrement(null);
        } else if (result && result.errorCode === "INVALID_INPUT") {
            setWaitlistAlert({ type: "danger", message: "Les informations saisies sont invalides. Vérifiez le nombre de places (le total ne doit pas dépasser la capacité de l'activité)." });
        } else {
            setWaitlistAlert({ type: "danger", message: "Une erreur est survenue lors de la mise à jour. Veuillez réessayer." });
        }
        dispatch("clearIncrementWaitingListResult");
        setIncrementSubmitting(false);
    });

    const onTimeClick = (booking, e) => {
        e.preventDefault();
        handleBooking(booking);
    };

    const onAvailabilityClick = (availability, e) => {
        e.preventDefault();
        const itemHoursElement = document.querySelector("#item-hours");
        setSelectedDate(moment(availability.date).utcOffset(availability.schedule.utcOffset).format("DD/MM/YYYY"));
        window.scrollTo({
            left: 0,
            top: itemHoursElement.offsetTop
        });
    };

    const onJoinWaitlist = (availability, e) => {
        e.preventDefault();
        setWaitlistSlot(availability);
        setWaitlistEmail((state.currentUser && state.currentUser.emailAddress) || "");
        setWaitlistFirstName("");
        setWaitlistLastName("");
        setWaitlistQty(1);
        setWaitlistAlert(null);
        setPendingIncrement(null);
    };

    const submitWaitlist = async (e) => {
        e.preventDefault();
        const slot = waitlistSlot();
        if (!slot) return;

        // Les utilisateurs connectés n'ont pas besoin de saisir leur email
        // ni leur nom (leur Customer est utilisé au traitement).
        if (!state.currentUser) {
            if (!waitlistEmail().trim()) {
                setWaitlistAlert({ type: "danger", message: "Veuillez saisir votre adresse e-mail." });
                return;
            }
            if (!waitlistFirstName().trim() || !waitlistLastName().trim()) {
                setWaitlistAlert({ type: "danger", message: "Veuillez saisir votre prénom et votre nom." });
                return;
            }
        }

        const qty = parseInt(waitlistQty(), 10);
        if (!Number.isInteger(qty) || qty < 1) {
            setWaitlistAlert({ type: "danger", message: "Le nombre de places doit être un entier positif." });
            return;
        }

        setWaitlistSubmitting(true);
        dispatch("joinWaitingList", {
            scheduleId: slot.schedule.id,
            date: moment(slot.date).utc().set('millisecond', 0).toDate(),
            quantity: qty,
            email: state.currentUser ? null : waitlistEmail().trim(),
            firstName: state.currentUser ? null : waitlistFirstName().trim(),
            lastName: state.currentUser ? null : waitlistLastName().trim(),
        });
    };

    const cancelWaitlist = () => {
        setWaitlistSlot(null);
        setWaitlistAlert(null);
        setPendingIncrement(null);
    };

    const confirmIncrement = async (e) => {
        e.preventDefault();
        const pending = pendingIncrement();
        if (!pending || !pending.slot) return;
        setIncrementSubmitting(true);
        dispatch("incrementWaitingList", {
            scheduleId: pending.slot.schedule.id,
            date: moment(pending.slot.date).utc().set('millisecond', 0).toDate(),
            additionalQuantity: pending.additionalQuantity,
            email: pending.email,
        });
    };

    const cancelIncrement = () => {
        setPendingIncrement(null);
    };

    const Availability = ({availability}) => {

        const date = moment(availability.date).utcOffset(availability.schedule.utcOffset);
        if (currentDate() !== date.format("DD/MM/YYYY")) {

            setCurrentDate(date.format("DD/MM/YYYY"));
            date.locale('fr');

            return (
                <h3 onClick={[onAvailabilityClick, availability]} class="d-inline-flex flex-column text-center" classList={{active: moment(availability.date).utcOffset(availability.schedule.utcOffset).format("DD/MM/YYYY") === selectedDate()}}>
                    <span class="dayofweek text-uppercase">{date.format("dddd")}</span>
                    <span class="day">{date.format("DD")}</span>
                    <span class="month text-uppercase">{date.format("MMMM")}</span>
                    <span class="year">{date.format("YYYY")}</span>
                </h3>
            );

        }

    };

    const Times = ({availability, selectedDate}) => (
        <Show when={moment(availability.date).utcOffset(availability.schedule.utcOffset).format("DD/MM/YYYY") === selectedDate()}>
            {availability.available > 0 ? (
                <div class="btn btn-white">
                    <a class="d-flex justify-items-center flex-column align-items-center" href="#" onClick={[onTimeClick, {channelToken: params.channelToken, activityId: params.activityId, scheduleId: availability.schedule.id, scheduleFrom: availability.schedule.from, scheduleTo: availability.schedule.to, utcOffset: availability.schedule.utcOffset, date: availability.date, available: availability.available, variantId: state.activities[params.activityId].variants[0].id, price: state.activities[params.activityId].variants[0].priceWithTax}]}><span class="time">{moment.utc(availability.date).utcOffset(availability.schedule.utcOffset).format("HH:mm")}</span><span class="availabilities">{availability.available} places disponibles</span></a>
                </div>
            ) : (
                <div class="btn btn-disabled">
                    <div class="d-flex justify-items-center flex-column align-items-center">
                        <span class="time">{moment.utc(availability.date).utcOffset(availability.schedule.utcOffset).format("HH:mm")}</span>
                        <span class="availabilities">0 place disponible</span>
                        <a href="#" onClick={[onJoinWaitlist, availability]} class="mt-2 text-decoration-none">
                            <span class="badge bg-info text-white"><FontAwesomeIcon icon={faClock} className="mr-1" /> Liste d'attente</span>
                        </a>
                    </div>
                </div>
            )}
        </Show>
    );

    return (
        <div class="container activity">
            <Show when={state.loading.availabilities === false} fallback={<p>Chargement des dates...</p>}>
                <h2>Sélectionnez votre date pour <span class="d-block d-md-inline">&laquo; {state.activities[params.activityId] ? state.activities[params.activityId].name : ''} &raquo; :</span></h2>
                <div id="items" className="d-flex align-items-center justify-content-center flex-wrap">
                    <For
                        each={state.availabilities[params.activityId]}
                        fallback={"Aucune date disponible pour cette activité"}>
                        {availability => (
                            <Availability availability={availability} />
                        )}
                    </For>
                    <div id="item-hours" class="w-100">
                        {selectedDate() ? (<h2>Choisir une heure pour la date sélectionnée ci-dessus :</h2>) : (<></>)}
                        <div class="d-flex justify-content-center flex-wrap align-items-center">
                            <For each={state.availabilities[params.activityId]}>
                                {availability => (
                                    <Times availability={availability} selectedDate={selectedDate} onSetBooking={handleBooking} />
                                )}
                            </For>
                        </div>
                    </div>
                </div>

                <Show when={waitlistSlot() && !pendingIncrement()}>
                    <div class="mt-4">
                        <div class="card">
                            <div class="card-body">
                                <h4 class="card-title">Inscription à la liste d'attente</h4>
                                <p class="card-text">
                                    {moment.utc(waitlistSlot().date).utcOffset(waitlistSlot().schedule.utcOffset).locale('fr').format('dddd DD MMMM YYYY à HH:mm')}
                                </p>
                                <Form onSubmit={submitWaitlist}>
                                    <Show when={!state.currentUser}>
                                        <Form.Group class="mb-3">
                                            <Form.Label>Prénom</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Prénom"
                                                value={waitlistFirstName()}
                                                onInput={(e) => setWaitlistFirstName(e.currentTarget.value)}
                                                maxlength="100"
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group class="mb-3">
                                            <Form.Label>Nom</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Nom"
                                                value={waitlistLastName()}
                                                onInput={(e) => setWaitlistLastName(e.currentTarget.value)}
                                                maxlength="100"
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group class="mb-3">
                                            <Form.Label>Adresse e-mail</Form.Label>
                                            <Form.Control
                                                type="email"
                                                placeholder="votre@email.fr"
                                                value={waitlistEmail()}
                                                onInput={(e) => setWaitlistEmail(e.currentTarget.value)}
                                                required
                                            />
                                        </Form.Group>
                                    </Show>
                                    <Form.Group class="mb-3">
                                        <Form.Label>Nombre de places</Form.Label>
                                        <Form.Control
                                            type="number"
                                            min="1"
                                            max="10"
                                            value={waitlistQty()}
                                            onInput={(e) => setWaitlistQty(parseInt(e.currentTarget.value) || 1)}
                                        />
                                    </Form.Group>
                                    <div class="d-flex gap-2">
                                        <Button type="submit" variant="primary" disabled={waitlistSubmitting()}><FontAwesomeIcon icon={faClock} className="mr-2" /> {waitlistSubmitting() ? "Inscription..." : "S'inscrire"}</Button>
                                        <Button variant="outline-secondary" onClick={cancelWaitlist} disabled={waitlistSubmitting()}>Annuler</Button>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    </div>
                </Show>

                <Show when={pendingIncrement()}>
                    <div class="mt-3">
                        <Alert variant="info">
                            <p class="mb-2">Vous avez déjà <strong>{pendingIncrement().existingQuantity}</strong> place(s) sur la liste d'attente pour ce créneau.</p>
                            <p class="mb-3">Souhaitez-vous ajouter <strong>{pendingIncrement().additionalQuantity}</strong> place(s) ? Total : <strong>{pendingIncrement().proposedTotal}</strong> place(s).</p>
                            <div class="d-flex gap-2">
                                <Button variant="primary" onClick={confirmIncrement} disabled={incrementSubmitting()}><FontAwesomeIcon icon={faClock} className="mr-2" /> {incrementSubmitting() ? "Mise à jour..." : "Confirmer"}</Button>
                                <Button variant="outline-secondary" onClick={cancelIncrement} disabled={incrementSubmitting()}>Annuler</Button>
                            </div>
                        </Alert>
                    </div>
                </Show>

                <Show when={waitlistAlert()}>
                    <div class="mt-3">
                        <Alert variant={waitlistAlert().type}>
                            {waitlistAlert().message}
                        </Alert>
                    </div>
                </Show>

                <div className="mt-5"><Link className="btn btn-primary" href={`/institution/${params.channelToken}`}><FontAwesomeIcon icon={faChevronLeft} className={'mr-2'}/> Retour</Link></div>
            </Show>
        </div>
    );
};
